import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Divider,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { motion } from 'framer-motion';

const FeeManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Fee Structure State
  const [feeStructures, setFeeStructures] = useState([]);
  const [feeStructureDialog, setFeeStructureDialog] = useState(false);
  const [editingFeeStructure, setEditingFeeStructure] = useState(null);
  const [feeStructureForm, setFeeStructureForm] = useState({
    class: '',
    academic_year: new Date().getFullYear(),
    tuition_fee: '',
    admission_fee: '',
    library_fee: '',
    lab_fee: '',
    sports_fee: '',
    transport_fee: '',
    hostel_fee: '',
    other_fee: '',
    total_fee: '',
    due_date: '',
    late_fee: ''
  });
  
  // Student Fees State
  const [studentFees, setStudentFees] = useState([]);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    transaction_id: '',
    remarks: ''
  });
  
  // Statistics State
  const [stats, setStats] = useState({
    totalFees: 0,
    totalPaid: 0,
    totalDue: 0,
    totalStudents: 0
  });

  useEffect(() => {
    fetchStats();
    fetchFeeStructures();
    fetchStudentFees();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/fees/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching fee stats:', error);
    }
  };

  const fetchFeeStructures = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/fees/structures');
      if (response.data.success) {
        setFeeStructures(response.data.data);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch fee structures', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentFees = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/fees/students?page=${page + 1}&limit=${rowsPerPage}&search=${searchTerm}`);
      if (response.data.success) {
        setStudentFees(response.data.data);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch student fees', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFeeStructure = async () => {
    try {
      const url = editingFeeStructure 
        ? `/api/fees/structures/${editingFeeStructure.structure_id}`
        : '/api/fees/structures';
      const method = editingFeeStructure ? 'put' : 'post';
      
      const response = await axios[method](url, feeStructureForm);
      if (response.data.success) {
        enqueueSnackbar(
          `Fee structure ${editingFeeStructure ? 'updated' : 'created'} successfully`,
          { variant: 'success' }
        );
        setFeeStructureDialog(false);
        resetFeeStructureForm();
        fetchFeeStructures();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar('Failed to save fee structure', { variant: 'error' });
    }
  };

  const handleDeleteFeeStructure = async (structureId) => {
    if (window.confirm('Are you sure you want to delete this fee structure?')) {
      try {
        const response = await axios.delete(`/api/fees/structures/${structureId}`);
        if (response.data.success) {
          enqueueSnackbar('Fee structure deleted successfully', { variant: 'success' });
          fetchFeeStructures();
          fetchStats();
        }
      } catch (error) {
        enqueueSnackbar('Failed to delete fee structure', { variant: 'error' });
      }
    }
  };

  const handlePayment = async () => {
    try {
      const response = await axios.post(`/api/fees/payments`, {
        student_id: selectedStudent.student_id,
        ...paymentForm
      });
      if (response.data.success) {
        enqueueSnackbar('Payment recorded successfully', { variant: 'success' });
        setPaymentDialog(false);
        resetPaymentForm();
        fetchStudentFees();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar('Failed to record payment', { variant: 'error' });
    }
  };

  const resetFeeStructureForm = () => {
    setFeeStructureForm({
      class: '',
      academic_year: new Date().getFullYear(),
      tuition_fee: '',
      admission_fee: '',
      library_fee: '',
      lab_fee: '',
      sports_fee: '',
      transport_fee: '',
      hostel_fee: '',
      other_fee: '',
      total_fee: '',
      due_date: '',
      late_fee: ''
    });
    setEditingFeeStructure(null);
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      amount: '',
      payment_method: 'cash',
      transaction_id: '',
      remarks: ''
    });
    setSelectedStudent(null);
  };

  const calculateTotalFee = () => {
    const fees = [
      parseFloat(feeStructureForm.tuition_fee) || 0,
      parseFloat(feeStructureForm.admission_fee) || 0,
      parseFloat(feeStructureForm.library_fee) || 0,
      parseFloat(feeStructureForm.lab_fee) || 0,
      parseFloat(feeStructureForm.sports_fee) || 0,
      parseFloat(feeStructureForm.transport_fee) || 0,
      parseFloat(feeStructureForm.hostel_fee) || 0,
      parseFloat(feeStructureForm.other_fee) || 0
    ];
    return fees.reduce((sum, fee) => sum + fee, 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'partial': return 'warning';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Fee Management
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      ${stats.totalFees?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="body2">Total Fees</Typography>
                  </Box>
                  <AccountBalanceIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      ${stats.totalPaid?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="body2">Total Paid</Typography>
                  </Box>
                  <PaymentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      ${stats.totalDue?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="body2">Total Due</Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {stats.totalStudents || 0}
                    </Typography>
                    <Typography variant="body2">Total Students</Typography>
                  </Box>
                  <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Fee Structures" />
          <Tab label="Student Fees" />
        </Tabs>
      </Paper>

      {/* Fee Structures Tab */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h6">Fee Structures</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetFeeStructureForm();
                setFeeStructureDialog(true);
              }}
            >
              Add Fee Structure
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Class</TableCell>
                  <TableCell>Academic Year</TableCell>
                  <TableCell>Tuition Fee</TableCell>
                  <TableCell>Total Fee</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feeStructures.map((structure) => (
                  <TableRow key={structure.structure_id}>
                    <TableCell>{structure.class}</TableCell>
                    <TableCell>{structure.academic_year}</TableCell>
                    <TableCell>${structure.tuition_fee}</TableCell>
                    <TableCell>${structure.total_fee}</TableCell>
                    <TableCell>{new Date(structure.due_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={structure.is_active ? 'Active' : 'Inactive'}
                        color={structure.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingFeeStructure(structure);
                            setFeeStructureForm(structure);
                            setFeeStructureDialog(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteFeeStructure(structure.structure_id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Student Fees Tab */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h6">Student Fees</Typography>
            <TextField
              size="small"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Total Fee</TableCell>
                  <TableCell>Paid Amount</TableCell>
                  <TableCell>Due Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {studentFees.map((student) => (
                  <TableRow key={student.student_id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          {getInitials(student.first_name, student.last_name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {student.first_name} {student.last_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {student.student_code}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{student.class} - {student.section}</TableCell>
                    <TableCell>${student.total_fee}</TableCell>
                    <TableCell>${student.paid_amount}</TableCell>
                    <TableCell>${student.due_amount}</TableCell>
                    <TableCell>
                      <Chip
                        label={student.payment_status}
                        color={getStatusColor(student.payment_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Record Payment">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedStudent(student);
                            setPaymentForm({ ...paymentForm, amount: student.due_amount });
                            setPaymentDialog(true);
                          }}
                          color="primary"
                        >
                          <PaymentIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Receipt">
                        <IconButton size="small" color="secondary">
                          <ReceiptIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={studentFees.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          />
        </Paper>
      )}

      {/* Fee Structure Dialog */}
      <Dialog open={feeStructureDialog} onClose={() => setFeeStructureDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFeeStructure ? 'Edit Fee Structure' : 'Add Fee Structure'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select
                  value={feeStructureForm.class}
                  onChange={(e) => setFeeStructureForm({ ...feeStructureForm, class: e.target.value })}
                >
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map((cls) => (
                    <MenuItem key={cls} value={cls}>Class {cls}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Academic Year"
                type="number"
                value={feeStructureForm.academic_year}
                onChange={(e) => setFeeStructureForm({ ...feeStructureForm, academic_year: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tuition Fee"
                type="number"
                value={feeStructureForm.tuition_fee}
                onChange={(e) => setFeeStructureForm({ ...feeStructureForm, tuition_fee: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Admission Fee"
                type="number"
                value={feeStructureForm.admission_fee}
                onChange={(e) => setFeeStructureForm({ ...feeStructureForm, admission_fee: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Library Fee"
                type="number"
                value={feeStructureForm.library_fee}
                onChange={(e) => setFeeStructureForm({ ...feeStructureForm, library_fee: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lab Fee"
                type="number"
                value={feeStructureForm.lab_fee}
                onChange={(e) => setFeeStructureForm({ ...feeStructureForm, lab_fee: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sports Fee"
                type="number"
                value={feeStructureForm.sports_fee}
                onChange={(e) => setFeeStructureForm({ ...feeStructureForm, sports_fee: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Transport Fee"
                type="number"
                value={feeStructureForm.transport_fee}
                onChange={(e) => setFeeStructureForm({ ...feeStructureForm, transport_fee: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hostel Fee"
                type="number"
                value={feeStructureForm.hostel_fee}
                onChange={(e) => setFeeStructureForm({ ...feeStructureForm, hostel_fee: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Other Fee"
                type="number"
                value={feeStructureForm.other_fee}
                onChange={(e) => setFeeStructureForm({ ...feeStructureForm, other_fee: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={feeStructureForm.due_date}
                onChange={(e) => setFeeStructureForm({ ...feeStructureForm, due_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Late Fee"
                type="number"
                value={feeStructureForm.late_fee}
                onChange={(e) => setFeeStructureForm({ ...feeStructureForm, late_fee: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Total Fee"
                type="number"
                value={calculateTotalFee()}
                disabled
                helperText="Automatically calculated from individual fees"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeeStructureDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveFeeStructure} variant="contained">
            {editingFeeStructure ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Record Payment - {selectedStudent?.first_name} {selectedStudent?.last_name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Amount"
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="online">Online Payment</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Transaction ID"
                value={paymentForm.transaction_id}
                onChange={(e) => setPaymentForm({ ...paymentForm, transaction_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={3}
                value={paymentForm.remarks}
                onChange={(e) => setPaymentForm({ ...paymentForm, remarks: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>Cancel</Button>
          <Button onClick={handlePayment} variant="contained">
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeManagement;
